/**
 * flow-step-shape.ts — the ONE place the "is this an action step?" fact lives.
 *
 * THE SHAPE IS A FACT, NOT A PREFERENCE, and it is read from the runtime:
 *
 *   cogo-runtime-v2/src/action-flow-engine.ts:872   const stepType = step.type || "basic"
 *                                          :874     case "basic":      executeBasic(...)
 *                                          :880     case "expression": executeBasic(...)
 *
 * So `type` is OPTIONAL on an action step — a step carrying only `action` IS a
 * basic step — and `expression` is an ACTION-carrying type, not a control type.
 * `cogo-schema/v4/flow.cogo.schema.json`'s `$defs.BasicStep` declares
 * `required: ["action"]` to match, and `cogo-validator`'s
 * `stage-actionflow.ts:908` computes the identical default
 * (`effectiveType = step.type ?? (step.action ? "basic" : undefined)`).
 *
 * WHY THIS MODULE EXISTS RATHER THAN A LOCAL CHECK PER PACKAGE. Task 4 first
 * repaired `lower-walker.ts` alone. A review then found FOUR more live callers
 * still demanding a literal `step.type === "basic"`, each of which HARD-THROWS
 * on the shape the schema now declares valid — and 100% of generated flow steps
 * are bare-`action` (2,183 of 2,183 in the committed corpus), so every one of
 * them threw on every generated app:
 *
 *   @cogo/publisher-next          src/lower-application.ts   NEXT_PUBLISHER_UNSUPPORTED_FLOW_STEP
 *   @cogo/publisher-cogo-web      src/lower-application.ts   COGO_WEB_PUBLISHER_UNSUPPORTED_ACTION
 *   @cogo/publishing-application  src/application-behavior-proof.ts  ×2
 *
 * That is the census's own lesson landing on this task: "a rule only one caller
 * applies is wrong for the next caller." `@cogo/publisher-core` is a dependency
 * of all four (and of `@cogo/publisher-newwon`), so this is the one import they
 * can share.
 */

/** Step types that CARRY an action and take `params`. */
export const ACTION_STEP_TYPES: ReadonlySet<string> = new Set([
  "basic",
  // Corpus alias accepted by cogo-validator's stage-actionflow.ts.
  "action",
  // Legacy alias; the runtime routes `case "expression"` to executeBasic.
  "expression",
]);

/** Step types the runtime dispatches on `type` ALONE — they never read `action`. */
export const CONTROL_STEP_TYPES: ReadonlySet<string> = new Set([
  "conditional",
  "loop",
  "parallel",
  "tryCatch",
  "switch",
  "stage",
  "saga",
]);

/**
 * The step type the RUNTIME will use, applying its own default. Returns
 * `undefined` only for a step that is neither typed nor action-bearing.
 */
export function effectiveStepType(step: {
  type?: unknown;
  action?: unknown;
}): string | undefined {
  if (typeof step?.type === "string" && step.type.length > 0) return step.type;
  return typeof step?.action === "string" && step.action.length > 0
    ? "basic"
    : undefined;
}

/**
 * True iff this step is an ACTION step the runtime will execute through
 * `executeBasic` — i.e. it carries a non-empty `action`, and either no `type`
 * at all or one of the action-carrying types.
 *
 * Narrows so callers can drop their `as CogoActionStep & { action: string }`
 * casts.
 */
export function isActionStep<T extends { type?: unknown; action?: unknown }>(
  step: T,
): step is T & { action: string } {
  if (typeof step?.action !== "string" || step.action.length === 0) return false;
  const type = effectiveStepType(step);
  return type !== undefined && ACTION_STEP_TYPES.has(type);
}
